import { useStyletron } from '@thanx/uikit/theme'
import BrandAndroid from 'components/Icons/BrandAndroid'
import BrandApple from 'components/Icons/BrandApple'
import Information from 'components/Icons/Information'
import Smartphone from 'components/Icons/Smartphone'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import type { PublishedAppVersion, PublishedAppVersions } from 'models/Merchant'
import React from 'react'
import type { StyleObject } from 'styletron-standard'

const t = buildTranslate(
  'order_ratings.settings.guest_experience.app_version_notice'
)

// FR-57: the Call-the-store shortcut "requires a minimum app version today",
// and guest app v63.0.0 is where it renders. Below it a merchant can save a
// setting no guest will ever see, which is the dependency FR-57 asks to be
// flagged where the setting is surfaced.
//
// Held as a version CODE, never a string: the API packs semver into one
// sortable integer (major * 1_000_000 + minor * 1_000 + patch), and comparing
// the display strings gets '54.1.1' against '63.0.0' wrong the moment a field
// goes double-digit. The "63.0.0" the merchant reads is derived back out of
// this single number by formatVersionCode, so the stated requirement and the
// enforced one cannot drift.
//
// Delete this component, its copy and its tests once the fleet's floor is
// past this version — the notice hides itself long before then (see the early
// return below), so nothing breaks if that clean-up lags.
const REQUIRED_APP_VERSION_CODE = 63_000_000

const MAJOR_FACTOR = 1_000_000
const MINOR_FACTOR = 1_000

// Every value below is measured off prototype `order-feedback-landing-page`
// v2 version 83 (design-audit-v83, sections 1-3), which is the version that
// introduced this treatment. v83 draws it as ONE inline meta line inside the
// Call-the-store row's text column — bare text on the card, no block, no
// background — plus a tooltip that only opens on hover or focus.
const META_MARGIN_TOP = '10px'
const META_GAP = '6px'
const META_FONT_SIZE = '12.5px'
const META_LINE_HEIGHT = '18.75px'
const META_FONT_WEIGHT = 400
const META_COLOR = palette.grey70
const LABEL_FONT_WEIGHT = 700
const LABEL_LETTER_SPACING = '0.2px'
// Smartphone is the one icon in this repo whose `size` sets the HEIGHT and
// lets the width follow the portrait viewBox, so 14 renders the prototype's
// 10.2 x 14 glyph rather than a squared-off 14 x 14.
const PHONE_ICON_SIZE = 14
const PHONE_NUDGE_TOP = '0.5px'
const INFO_ICON_SIZE = 15

const TOOLTIP_WIDTH = '270px'
const TOOLTIP_RADIUS = '4px'
const TOOLTIP_PADDING_VERTICAL = '12px'
const TOOLTIP_PADDING_HORIZONTAL = '15px'
// The only value in the design with no palette token behind it: palette.ts
// carries no shadow scale at all, so the prototype's literal is kept and
// named rather than approximated with a token that means something else.
const TOOLTIP_SHADOW = '0 4px 16px rgba(20, 28, 38, 0.18)'
// Bottom edge 10px above the top of the info icon; left edge 12px to the LEFT
// of it, which is what puts the arrow under the icon (see ARROW_LEFT).
const TOOLTIP_BOTTOM = 'calc(100% + 10px)'
const TOOLTIP_LEFT = '-12px'
const TOOLTIP_Z_INDEX = 40
const TOOLTIP_HIDDEN_TRANSFORM = 'translateY(4px)'
const TOOLTIP_SHOWN_TRANSFORM = 'translateY(0)'
const TOOLTIP_TRANSITION_DURATION = '0.15s'
const TOOLTIP_TRANSITION_TIMING = 'ease'
// A 0x0 box with equal borders on all four sides renders each border as a
// triangle; colouring only the TOP one leaves a 14 x 7 triangle pointing down.
const ARROW_BORDER_WIDTH = '7px'
const ARROW_LEFT = '18px'
const TOOLTIP_TITLE_FONT_SIZE = '12.5px'
const TOOLTIP_TITLE_LINE_HEIGHT = '18.125px'
const TOOLTIP_TITLE_FONT_WEIGHT = 700
const TOOLTIP_ROW_FONT_SIZE = '12px'
const TOOLTIP_ROW_LINE_HEIGHT = '18px'
const TOOLTIP_ROW_GAP = '7px'
const TOOLTIP_ROW_MARGIN_TOP = '7px'
const TOOLTIP_ROW_ICON_SIZE = 16
const TOOLTIP_ROW_ICON_MARGIN_TOP = '1px'
const TOOLTIP_PLATFORM_FONT_WEIGHT = 700

// The reveal is CSS only, exactly as the design is: the prototype binds no
// script to the gate, and a hover/focus tooltip that needs React state stops
// working the moment the component re-renders mid-interaction. Styletron
// recurses into any nested key that STARTS with a colon and appends it raw to
// the generated class, so `:hover .<class>` compiles to a real descendant
// rule — which is why the tooltip needs a stable class name of its own to be
// addressed by, on top of its generated one.
const TOOLTIP_CLASS = 'order-feedback-app-version-tooltip'
const TOOLTIP_ID = 'order-feedback-app-version-tooltip'

type Platform = 'ios' | 'android'

// iOS first: it is the platform that lags in practice, so a merchant reads
// their worst news first. v83 draws the rows in this order too.
const PLATFORMS: Platform[] = ['ios', 'android']

const PLATFORM_ICONS: Record<Platform, typeof BrandApple> = {
  ios: BrandApple,
  android: BrandAndroid,
}

type PlatformRelease = {
  platform: Platform
  release: PublishedAppVersion
}

// Inverse of the API's packing, used for the required version only. Each
// platform's own current version is shown from the API's `version` string,
// which is the authoritative display form.
const formatVersionCode = (code: number): string => {
  const major = Math.floor(code / MAJOR_FACTOR)
  const minor = Math.floor((code % MAJOR_FACTOR) / MINOR_FACTOR)
  const patch = code % MINOR_FACTOR

  return `${major}.${minor}.${patch}`
}

// Drops the platforms with no published release. `null` there means the
// merchant has no app on that store, so there is no version to report and
// nothing they could update.
const platformReleases = (
  versions: PublishedAppVersions | undefined
): PlatformRelease[] =>
  PLATFORMS.flatMap(platform => {
    const release = versions?.[platform]

    return release ? [{ platform, release }] : []
  })

const isBehind = (release: PublishedAppVersion): boolean =>
  release.version_code < REQUIRED_APP_VERSION_CODE

// v83 bolds the platform name where the tooltip row first names it, and only
// there. The copy itself has to stay one sentence in one locale string —
// react-redux-i18n interpolates strings, not React nodes, so `%{platform}`
// cannot carry markup — which leaves splitting the finished sentence around
// its first mention of that same interpolated label. Deterministic, because
// the label is what was interpolated in: the sentence provably contains it.
const splitOnFirst = (
  sentence: string,
  term: string
): [string, string, string] => {
  const at = sentence.indexOf(term)

  if (at < 0) return ['', '', sentence]

  return [sentence.slice(0, at), term, sentence.slice(at + term.length)]
}

type Props = {
  // Straight off the full merchant payload. `undefined` while the field is
  // unknown — either the merchant has not loaded or the nexus change that
  // adds it has not deployed — which reads the same as "no published app"
  // here and renders nothing, rather than guessing a version.
  publishedAppVersions?: PublishedAppVersions
}

// FR-57's app-version dependency, drawn as prototype v83 draws it: an inline
// meta line in the Call-the-store row's own text column, between the row's
// description and its written-feedback gate note, with the detail folded into
// a hover/focus tooltip. v83 puts it on that row only — the Acknowledgment
// message row shares the same floor but carries no version messaging there.
//
// v83 depicts exactly one state, both platforms behind, and supplies no
// compliant state at all. Two behaviours are therefore carried over from the
// component this replaces rather than read off the design:
//
//   - it still hides itself once every published platform clears the floor,
//     because a warning that never retires is worse than no warning; and
//   - it still reports each platform truthfully, because iOS and Android ship
//     separately and a merchant is routinely compliant on one and behind on
//     the other. v83 only ever had one platform state to draw.
//
// Deliberately NOT shared with the Live Updates scene's notice of the same
// name: that one lives in another scene, which this repo forbids importing
// across.
const AppVersionNotice = ({ publishedAppVersions }: Props) => {
  const [css] = useStyletron()

  const releases = platformReleases(publishedAppVersions)

  // One early return covers every quiet case, which is the point of the
  // notice: it retires itself instead of waiting for someone to delete it.
  //
  //   - No branded app (both platforms null, 429 of 759 merchants today):
  //     nothing published, nothing to update, and a notice about updating an
  //     app they do not have is pure noise.
  //   - One platform only, already at or past the floor: same.
  //   - Both platforms at or past the floor: the dependency is satisfied and
  //     saying so would be telling the merchant about a constraint that no
  //     longer constrains them.
  //   - Field absent (older API, picker/minimal payload): unknown is not the
  //     same as behind, so it stays quiet rather than accusing a compliant
  //     merchant.
  if (!releases.some(({ release }) => isBehind(release))) return null

  const requiredVersion = formatVersionCode(REQUIRED_APP_VERSION_CODE)

  const tooltipClass = `${TOOLTIP_CLASS} ${css({
    position: 'absolute',
    bottom: TOOLTIP_BOTTOM,
    left: TOOLTIP_LEFT,
    width: TOOLTIP_WIDTH,
    backgroundColor: palette.grey10,
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    borderTopLeftRadius: TOOLTIP_RADIUS,
    borderTopRightRadius: TOOLTIP_RADIUS,
    borderBottomRightRadius: TOOLTIP_RADIUS,
    borderBottomLeftRadius: TOOLTIP_RADIUS,
    boxShadow: TOOLTIP_SHADOW,
    paddingTop: TOOLTIP_PADDING_VERTICAL,
    paddingBottom: TOOLTIP_PADDING_VERTICAL,
    paddingLeft: TOOLTIP_PADDING_HORIZONTAL,
    paddingRight: TOOLTIP_PADDING_HORIZONTAL,
    opacity: 0,
    visibility: 'hidden',
    transform: TOOLTIP_HIDDEN_TRANSFORM,
    transitionProperty: 'opacity, visibility, transform',
    transitionDuration: TOOLTIP_TRANSITION_DURATION,
    transitionTimingFunction: TOOLTIP_TRANSITION_TIMING,
    zIndex: TOOLTIP_Z_INDEX,
    textAlign: 'left',
    fontWeight: META_FONT_WEIGHT,
    whiteSpace: 'normal',
    color: META_COLOR,
    // The arrow: `top: 100%` hangs it off the tooltip's bottom edge, and its
    // 14px span runs from 6px to 20px right of the icon's left edge, so it
    // lands under the icon rather than under the tooltip's own corner.
    '::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: ARROW_LEFT,
      borderTopWidth: ARROW_BORDER_WIDTH,
      borderRightWidth: ARROW_BORDER_WIDTH,
      borderBottomWidth: ARROW_BORDER_WIDTH,
      borderLeftWidth: ARROW_BORDER_WIDTH,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: palette.grey10,
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent',
    },
  })}`

  const shown: StyleObject = {
    opacity: 1,
    visibility: 'visible',
    transform: TOOLTIP_SHOWN_TRANSFORM,
  }

  // `cursor: help` and a reset of every button default, so the trigger is
  // pixel-identical to the design's bare `<span tabindex="0">` while still
  // being a real button: natively focusable, natively operable, and named.
  const triggerClass = css({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
    backgroundColor: 'transparent',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    paddingTop: '0',
    paddingRight: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    lineHeight: META_LINE_HEIGHT,
    [`:hover .${TOOLTIP_CLASS}`]: shown,
    [`:focus .${TOOLTIP_CLASS}`]: shown,
  })

  const rowClass = css({
    display: 'flex',
    alignItems: 'flex-start',
    gap: TOOLTIP_ROW_GAP,
    marginTop: TOOLTIP_ROW_MARGIN_TOP,
    fontSize: TOOLTIP_ROW_FONT_SIZE,
    lineHeight: TOOLTIP_ROW_LINE_HEIGHT,
    fontWeight: META_FONT_WEIGHT,
    color: META_COLOR,
  })

  const rowIconClass = css({
    display: 'flex',
    flex: 'none',
    marginTop: TOOLTIP_ROW_ICON_MARGIN_TOP,
  })

  const platformClass = css({
    display: 'inline',
    fontSize: 'inherit',
    fontWeight: TOOLTIP_PLATFORM_FONT_WEIGHT,
    color: palette.grey90,
  })

  return (
    <div
      data-testid="guest-experience-app-version-notice"
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        gap: META_GAP,
        marginTop: META_MARGIN_TOP,
        fontSize: META_FONT_SIZE,
        lineHeight: META_LINE_HEIGHT,
        fontWeight: META_FONT_WEIGHT,
        color: META_COLOR,
      })}
    >
      {/* Smartphone sets aria-hidden on its own <svg>; this wrapper is the
          0.5px optical nudge the design gives the glyph against the label. */}
      <span
        className={css({
          display: 'inline-flex',
          position: 'relative',
          top: PHONE_NUDGE_TOP,
        })}
      >
        <Smartphone fillColor={META_COLOR} size={PHONE_ICON_SIZE} />
      </span>
      <span
        className={css({
          fontWeight: LABEL_FONT_WEIGHT,
          letterSpacing: LABEL_LETTER_SPACING,
        })}
      >
        {t('label', { version: requiredVersion })}
      </span>
      <button
        type="button"
        className={triggerClass}
        aria-label={t('info_label')}
        aria-describedby={TOOLTIP_ID}
      >
        {/* Information does not set aria-hidden itself, and the button is
            already named, so the glyph is hidden here. */}
        <span aria-hidden="true" className={css({ display: 'flex' })}>
          <Information
            type="info"
            fillColor={palette.grey50}
            size={INFO_ICON_SIZE}
          />
        </span>
        <span
          id={TOOLTIP_ID}
          role="tooltip"
          data-testid="guest-experience-app-version-tooltip"
          className={tooltipClass}
        >
          <span
            className={css({
              display: 'block',
              fontSize: TOOLTIP_TITLE_FONT_SIZE,
              lineHeight: TOOLTIP_TITLE_LINE_HEIGHT,
              fontWeight: TOOLTIP_TITLE_FONT_WEIGHT,
              color: palette.grey90,
            })}
          >
            {t('lead', { version: requiredVersion })}
          </span>
          {releases.map(({ platform, release }) => {
            const PlatformIcon = PLATFORM_ICONS[platform]
            const label = t(`platforms.${platform}`)
            const [before, name, after] = splitOnFirst(
              t(isBehind(release) ? 'behind' : 'met', {
                platform: label,
                current: release.version,
              }),
              label
            )

            return (
              <span key={platform} className={rowClass}>
                <span className={rowIconClass}>
                  <PlatformIcon
                    color={META_COLOR}
                    size={TOOLTIP_ROW_ICON_SIZE}
                  />
                </span>
                <span>
                  {before}
                  <b className={platformClass}>{name}</b>
                  {after}
                </span>
              </span>
            )
          })}
        </span>
      </button>
    </div>
  )
}

export default AppVersionNotice
